<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useCssVar } from '@vueuse/core';

const props = withDefaults(defineProps<{
    ripples?: number;
    withLabel?: boolean;
  }>(), {
  ripples: 2,
  withLabel: true,
});

const showFirstRipple = computed(() => props.ripples > 0);
const showSecondRipple = computed(() => props.ripples > 1);
const showThirdRipple = computed(() => props.ripples > 2);

const svg = ref(null);

const viewBox = computed(() => {
  if (props.ripples === 0) {
    return {
      width: props.withLabel ? 127 : 42,
      height: 42,
      x: 63,
      y: 63,
    };
  }

  if (props.ripples === 1) {
    return {
      width: props.withLabel ? 140 : 67,
      height: 68,
      x: 50,
      y: 50,
    };
  }

  if (props.ripples === 2) {
    return {
      width: props.withLabel ? 163 : 115,
      height: 114,
      x: 27,
      y: 27,
    };
  }

  return {
    width: props.withLabel ? 190 : 170,
    height: 168,
    x: 0,
    y: 0,
  };
});

const primaryColor = ref(useCssVar('--primary-color').value);
const secondaryColor = ref(useCssVar('--secondary-color').value);
onMounted(() => {
  primaryColor.value = useCssVar('--bg-primary', svg.value).value;
  secondaryColor.value = useCssVar('--bg-secondary', svg.value).value;
});
</script>

<!-- eslint-disable max-len, vue/max-len -->
<template>
  <svg
    ref="svg"
    class="vrstar-logo"
    xmlns="http://www.w3.org/2000/svg"
    xmlns:xlink="http://www.w3.org/1999/xlink"
    :viewBox="`${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}`">
    <defs>
      <linearGradient
        id="linear-gradient"
        x1="0.5"
        x2="0.5"
        y2="1"
        gradientUnits="objectBoundingBox">
        <stop
          offset="0"
          :stop-color="primaryColor" />
        <stop
          offset="1"
          :stop-color="secondaryColor"
          stop-opacity="0" />
      </linearGradient>
      <linearGradient
        id="linear-gradient-3"
        x1="0.785"
        y1="0.236"
        x2="-0.164"
        y2="0.798"
        gradientUnits="objectBoundingBox">
        <stop
          offset="0"
          :stop-color="primaryColor" />
        <stop
          offset="1"
          :stop-color="secondaryColor"
          stop-opacity="0.341" />
      </linearGradient>
      <clipPath id="clip-path">
        <rect
          id="Rettangolo_9215"
          data-name="Rettangolo 9215"
          width="20.534"
          height="30.791"
          fill="none" />
      </clipPath>
    </defs>
    <g
      transform="translate(16367.268 23975.145)">
      <circle
        v-if="showThirdRipple"
        cx="84"
        cy="84"
        r="84"
        transform="translate(-16367.268 -23975.145)"
        opacity="0.351"
        fill="url(#linear-gradient)" />
      <circle
        v-if="showSecondRipple"
        cx="56.5"
        cy="56.5"
        r="56.5"
        transform="translate(-16339.59 -23947.465)"
        opacity="0.3"
        fill="url(#linear-gradient)" />
      <circle
        v-if="showFirstRipple"
        cx="33"
        cy="33"
        r="33"
        transform="translate(-16316.59 -23924.465)"
        opacity="0.3"
        fill="url(#linear-gradient-3)" />
      <ellipse
        cx="19.5"
        cy="20"
        rx="19.5"
        ry="20"
        transform="translate(-16302.591 -23911.465)"
        :fill="primaryColor" />
      <g
        transform="translate(-16292.992 -23906.859)"
        clip-path="url(#clip-path)">
        <path
          d="M10.267,0a10.261,10.261,0,0,0-8.89,15.4l8.89,15.4,8.89-15.4A10.261,10.261,0,0,0,10.267,0"
          class="pin" />
        <path
          d="M93.919,95l2.133,4.348,4.785.7-3.459,3.382.815,4.772-4.274-2.252L89.644,108.2l.815-4.772L87,100.047l4.778-.7Z"
          transform="translate(-83.642 -91.333)"
          class="pin-icon" />
      </g>
      <path
        v-if="withLabel"
        d="M7.95,19.75,12,6.25h3.7L10.075,23h-4.4L.075,6.25h3.8L7.85,19.75Zm19,3.25-3.775-6.025h-2.25V23H17.5V6.25h6.625a7.217,7.217,0,0,1,3.4.737,4.92,4.92,0,0,1,2.087,1.988A5.562,5.562,0,0,1,30.3,11.7a4.922,4.922,0,0,1-.9,2.888A5.311,5.311,0,0,1,26.875,16.5l4.15,6.5ZM20.925,9.25V14.1h2.95a3.185,3.185,0,0,0,2.263-.7,2.386,2.386,0,0,0,0-3.45,3.185,3.185,0,0,0-2.263-.7ZM33.35,18.525a5.675,5.675,0,0,0,1.8,1.2,5.589,5.589,0,0,0,2.225.45,3.319,3.319,0,0,0,1.838-.462,1.592,1.592,0,0,0,.712-1.437,1.678,1.678,0,0,0-.325-1.05,2.9,2.9,0,0,0-1.025-.775l-2.05-1.025a5.637,5.637,0,0,1-2.2-1.762,4.592,4.592,0,0,1-.725-2.688,4.681,4.681,0,0,1,.75-2.637,5.008,5.008,0,0,1,2.1-1.775,7.18,7.18,0,0,1,3.1-.638,8.828,8.828,0,0,1,3.2.55A6.187,6.187,0,0,1,45,7.875l-1.7,2.6a5.122,5.122,0,0,0-1.763-1.063A6.063,6.063,0,0,0,39.375,9a2.834,2.834,0,0,0-1.638.438A1.437,1.437,0,0,0,37.1,10.7a1.566,1.566,0,0,0,.3.988,2.913,2.913,0,0,0,.975.738l2.075.975a5.744,5.744,0,0,1,2.275,1.775,4.628,4.628,0,0,1,.75,2.75,5.094,5.094,0,0,1-.8,2.85,5.211,5.211,0,0,1-2.225,1.888,7.5,7.5,0,0,1-3.2.663,9.661,9.661,0,0,1-3.388-.562,6.338,6.338,0,0,1-2.437-1.538Zm16.725-4.85L49.25,18.55a5.323,5.323,0,0,0-.05.55,1.116,1.116,0,0,0,1.275,1.25,3.007,3.007,0,0,0,1.675-.525l.775,2.45a4.86,4.86,0,0,1-1.537.775,6.512,6.512,0,0,1-1.788.225,3.665,3.665,0,0,1-2.712-.987A3.565,3.565,0,0,1,45.9,19.65a5.48,5.48,0,0,1,.1-1.075l.825-4.9h-1.7l.475-2.85h1.7l.55-3.275H51.1l-.55,3.275h3.6l-.475,2.85Zm15.55,4.15L65.45,23H62.425l.1-2.425h-.1a4.18,4.18,0,0,1-3.925,2.75A3.683,3.683,0,0,1,55.55,22a5.671,5.671,0,0,1-1.125-3.775,9.5,9.5,0,0,1,.763-3.812,6.483,6.483,0,0,1,2.338-2.837,6.758,6.758,0,0,1,3.9-1.075,10.488,10.488,0,0,1,5.225,1.225Zm-7.85.2a3.06,3.06,0,0,0,.462,1.8,1.468,1.468,0,0,0,1.263.65,2.452,2.452,0,0,0,2.037-1.3,7.922,7.922,0,0,0,1.163-3.15l.4-2.375a5.41,5.41,0,0,0-.837-.3,4.176,4.176,0,0,0-1.013-.1,2.986,2.986,0,0,0-2.537,1.275A5.8,5.8,0,0,0,57.775,18.025ZM76.025,10.5a3.237,3.237,0,0,1,1.35.225L76.55,13.85a2.923,2.923,0,0,0-1.1-.175,2.872,2.872,0,0,0-2,.775,3.564,3.564,0,0,0-1.075,2.125L71.275,23H68l1.2-7.05.225-5.125H72.45L72.275,13.3h.1a4.775,4.775,0,0,1,1.513-2.075A3.469,3.469,0,0,1,76.025,10.5Z"
        transform="translate(-16255.59 -23906.037)"
        class="text" />
    </g>
  </svg>
</template>

<style scoped lang="scss">
.vrstar-logo {
  --bg-primary: var(--primary-color);
  --bg-secondary: var(--secondary-color);
  --pin-color: var(--secondary-color);
  --pin-icon-color: #fff;
  --text-color: #fff;

  .pin {
    fill: var(--pin-color);
  }

  .pin-icon {
    fill: var(--pin-icon-color);
  }

  .text {
    fill: var(--text-color);
  }
}
</style>
